import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, iif, Observable, of } from 'rxjs';
import { finalize, map, switchMap } from 'rxjs/operators';

import { environment } from '@env/environment';
import { Document, DocumentType, Site } from '@shared/models';
import { deserialize, serialize } from '@shared/models/base-helper';
import { SiteDocusignEnvelope } from '@shared/models/site-docusign-envelope.model';
import { SpinnerService } from '@shared/spinner/spinner.service';
import { WelcomeDocument } from '@shared/models/welcome-document.model';
import { ToastService } from '@shared/services/toast.service';
import { setActiveSite } from '@store/site/site.actions';
import { Store } from "@ngrx/store";
import * as fromApp from '@store';
import { UserActivityEventEnum } from '@shared/constants/user_activity.constants';
import { UserService } from '@core/api/user.service';

@Injectable({
  providedIn: 'root',
})
export class DocumentService {
  apiUrl = environment.apiUrl + '/documents/';
  docTypesApiUrl = environment.apiUrl + '/document_types/';

  docTypes: Array<DocumentType> = [];

  constructor(
    readonly http: HttpClient,
    readonly store: Store<fromApp.AppState>,
    readonly toastService: ToastService,
    readonly userService: UserService,
    readonly spinnerService: SpinnerService
  ) {
    this.getAllDocumentTypes().subscribe(docTypes => {this.docTypes = docTypes;});
  }

  getDocTypeId(name: string): number {
    const docType: DocumentType[] = this.docTypes.filter(dt => dt.name === name);
    return docType?.[0]?.id ?? null;
  }

  getAllDocumentTypes(): Observable<DocumentType[]> {
    return this.http.get<any[]>(this.docTypesApiUrl, { headers: new HttpHeaders().set('X-FC-Noauth', 'true') }).pipe(
      map(docTypes => docTypes.map((docType: any) => deserialize(docType, DocumentType))),
    );
  }

  uploadAndCreateDocument(site: Site, file: any, document: any, resetSite: boolean = false) {
    this.spinnerService.show('app-spinner', 'Uploading Document');
    this.uploadFile(site.identifier, file).subscribe(
      (fileKey: string) => {
        document.identifier = fileKey;
        this.createDocument(document).subscribe(() => {
            this.userService.logUserActivity({
              event: UserActivityEventEnum.SITE_MODIFICATION,
              details: `Document ${document.name} uploaded`,
              siteId: site.id
            });
            this.spinnerService.hide('app-spinner');
            if (resetSite) {
              this.store.dispatch(setActiveSite({ id: site.id }));
            }
          }
        );
      });
  }

  createDocument(document: Document) {
    return this.http
      .post<any>(`${this.apiUrl}document`, serialize(document))
      .pipe(map((serializedDocument: any) => deserialize(serializedDocument, Document)));
  }

  uploadFile(siteId, file) {
    let fileKey = null;
    return this.http.post<any>(`${this.apiUrl}site/${siteId}`, null).pipe(
      switchMap(resp => {
        const formData: FormData = new FormData();
        for (const key in resp.fields) {
          formData.append(key, resp.fields[key]);
        }
        formData.append('file', file);
        fileKey = formData.get('key') as string;
        fileKey = fileKey.split('/')[1];

        return iif(
          () => resp.url === 'MOCK_DO_NOT_UPLOAD',
          of(fileKey),
          this.http.post(resp.url, formData).pipe(map(() => fileKey))
        );
      })
    );
  }

  downloadDocument(siteId, documentId, fileName, open) {
    return firstValueFrom(
      this.http.get<any>(`${this.apiUrl}site/${siteId}/${documentId}?file_name=${fileName}&open=${open}`)
    );
  }

  downloadKMLData(siteId, documentId) {
    return this.http.get<any>(`${this.apiUrl}site/kml_data/${siteId}/${documentId}/`).pipe(
      map((result: any) => {
        return result;
      })
    );
  }

  deleteDocument(siteIdentifier: string, documentIdentifier: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}site/${siteIdentifier}/${documentIdentifier}`);
  }

  getOrphans(): Observable<number> {
    this.spinnerService.show('app-spinner');
    return this.http.get<any>(`${this.apiUrl}orphans`).pipe(
      map((result: any) => {
        return result['orphan_count'];
      }),
      finalize(() => this.spinnerService.hide('app-spinner'))
    );
  }

  deleteOrphans(): Observable<number> {
    this.spinnerService.show('app-spinner');
    return this.http.delete<any>(`${this.apiUrl}orphans`).pipe(
      map((result: any) => {
        return result['files_deleted'];
      }),
      finalize(() => this.spinnerService.hide('app-spinner'))
    );
  }

  updateDocument(document: any) {
    return this.http
      .patch<any>(`${this.apiUrl}document/${document.identifier}`, document)
      .pipe(map(serializedDocument => deserialize(serializedDocument, Document)));
  }

  voidEnvelope(envelopeId: string) {
    return this.http
      .post<any>(`${this.apiUrl}void_envelope/${envelopeId}/`, {})
      .pipe(map(docEnvelope => deserialize(docEnvelope, SiteDocusignEnvelope)));
  }

  resendEnvelope(envelopeId: string) {
    return this.http
      .post<any>(`${this.apiUrl}resend_envelope/${envelopeId}/`, {})
      .pipe(map(docEnvelope => deserialize(docEnvelope, SiteDocusignEnvelope)));
  }

  downloadWelcomeDocument(welcomeDocumentId: number) {
    return firstValueFrom(
      this.http.get<any>(`${this.apiUrl}welcome_document/${welcomeDocumentId}`)
    );
  }

  deleteWelcomeDoc(welcomeDocumentId: number): Observable<WelcomeDocument[]> {
    return this.http
      .delete<WelcomeDocument[]>(`${this.apiUrl}welcome_document/${welcomeDocumentId}`)
      .pipe(map(documents => documents.map((serializedDocument: any) => deserialize(serializedDocument, WelcomeDocument))));
  }

  uploadWelcomeDoc(enrollmentGroupId: number, file: any) {
    return new Promise<WelcomeDocument[]>(resolve => {
      const fr = new FileReader();
      fr.onload = (): void => {
        const body = {
          data: fr.result,
          name: file.name,
          content_type: file.type,
        };
        this.http
          .post<WelcomeDocument[]>(`${this.apiUrl}welcome_document/enrollment_group/${enrollmentGroupId}`, body)
          .pipe(map(documents => documents.map(serializedDocument => deserialize(serializedDocument, WelcomeDocument))))
          .subscribe(resolve);
      };
      fr.readAsDataURL(file);
    });
  }

  markDocumentAsContract(enrollmentGroupId: number, welcomeDocumentId: any) {
    return this.http
      .patch<WelcomeDocument[]>(`${this.apiUrl}welcome_document/enrollment_group/${enrollmentGroupId}/${welcomeDocumentId}`, {})
      .pipe(map(documents => documents.map((serializedDocument: any) => deserialize(serializedDocument, WelcomeDocument))));
  }
}
